/**
 * IndexedDB helpers for workflow recordings. Per-Berlin-user scoping
 * prevents cross-user data leak on shared browsers (spec § 4.1).
 *
 * DB name:        claude-cua-recordings-{userId}
 * Object stores:
 *   meta(id)          - {id, name, createdAt, durationMs, frameCount, ...}
 *   audio(recordingId) - {recordingId, blob}
 *   frame([rid, t])    - {recordingId, t, blob}
 */
const DB_VERSION = 1;
const _connections = new Map();   // userId → IDBDatabase

function dbName(userId) {
  return `claude-cua-recordings-${userId}`;
}

export function open(userId) {
  if (_connections.has(userId)) return Promise.resolve(_connections.get(userId));
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(userId), DB_VERSION);
    req.onupgradeneeded = (e) => {
      const idb = e.target.result;
      if (!idb.objectStoreNames.contains("meta"))  idb.createObjectStore("meta",  { keyPath: "id" });
      if (!idb.objectStoreNames.contains("audio")) idb.createObjectStore("audio", { keyPath: "recordingId" });
      if (!idb.objectStoreNames.contains("frame")) idb.createObjectStore("frame", { keyPath: ["recordingId", "t"] });
    };
    req.onsuccess = () => { _connections.set(userId, req.result); resolve(req.result); };
    req.onerror   = () => reject(req.error);
  });
}

/**
 * Close every cached IDBDatabase. Used in tests so deleteDatabase isn't
 * blocked, and could also be wired into a Berlin-user-switch hook to drop
 * the previous user's connection before opening the new one.
 */
export function closeAllConnections() {
  for (const idb of _connections.values()) {
    try { idb.close(); } catch (_) {}
  }
  _connections.clear();
}

async function tx(userId, stores, mode = "readonly") {
  const idb = await open(userId);
  return idb.transaction(stores, mode);
}

function awaitTx(t) {
  return new Promise((resolve, reject) => {
    t.oncomplete = resolve;
    t.onerror    = () => reject(t.error);
    t.onabort    = () => reject(t.error || new Error("tx aborted"));
  });
}

export async function putMeta(userId, meta) {
  const t = await tx(userId, ["meta"], "readwrite");
  t.objectStore("meta").put(meta);
  return awaitTx(t);
}

export async function getMeta(userId, id) {
  const t = await tx(userId, ["meta"]);
  return new Promise((resolve, reject) => {
    const r = t.objectStore("meta").get(id);
    r.onsuccess = () => resolve(r.result || null);
    r.onerror   = () => reject(r.error);
  });
}

export async function listMeta(userId) {
  const t = await tx(userId, ["meta"]);
  return new Promise((resolve, reject) => {
    const r = t.objectStore("meta").getAll();
    r.onsuccess = () => {
      const rows = (r.result || []).slice().sort((a, b) => b.createdAt - a.createdAt);
      resolve(rows);
    };
    r.onerror   = () => reject(r.error);
  });
}

export async function putAudio(userId, recordingId, blob) {
  const t = await tx(userId, ["audio"], "readwrite");
  t.objectStore("audio").put({ recordingId, blob });
  return awaitTx(t);
}

export async function getAudioBlob(userId, recordingId) {
  const t = await tx(userId, ["audio"]);
  return new Promise((resolve, reject) => {
    const r = t.objectStore("audio").get(recordingId);
    r.onsuccess = () => resolve(r.result ? r.result.blob : null);
    r.onerror   = () => reject(r.error);
  });
}

export async function putFrame(userId, recordingId, tMs, blob) {
  const trans = await tx(userId, ["frame"], "readwrite");
  trans.objectStore("frame").put({ recordingId, t: tMs, blob });
  return awaitTx(trans);
}

export async function listFrames(userId, recordingId) {
  const t = await tx(userId, ["frame"]);
  const store = t.objectStore("frame");
  const range = IDBKeyRange.bound([recordingId, -Infinity], [recordingId, Infinity]);
  const out = [];
  return new Promise((resolve, reject) => {
    const req = store.openCursor(range);
    req.onsuccess = () => {
      const c = req.result;
      if (c) { out.push({ t: c.value.t, blob: c.value.blob }); c.continue(); }
      else   { resolve(out); }
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deleteFrame(userId, recordingId, t) {
  const trans = await tx(userId, ["frame"], "readwrite");
  trans.objectStore("frame").delete([recordingId, t]);
  return awaitTx(trans);
}

export async function deleteRecording(userId, recordingId) {
  const t = await tx(userId, ["meta", "audio", "frame"], "readwrite");
  t.objectStore("meta").delete(recordingId);
  t.objectStore("audio").delete(recordingId);
  const fStore = t.objectStore("frame");
  const range = IDBKeyRange.bound([recordingId, -Infinity], [recordingId, Infinity]);
  fStore.openCursor(range).onsuccess = (e) => {
    const c = e.target.result;
    if (c) { c.delete(); c.continue(); }
  };
  return awaitTx(t);
}

export async function getFirstFrame(userId, recordingId) {
  const t = await tx(userId, ["frame"]);
  const store = t.objectStore("frame");
  const range = IDBKeyRange.bound([recordingId, -Infinity], [recordingId, Infinity]);
  return new Promise((resolve, reject) => {
    const req = store.openCursor(range, "next");
    req.onsuccess = () => {
      const c = req.result;
      resolve(c ? c.value.blob : null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function framesBytes(userId, recordingId) {
  const frames = await listFrames(userId, recordingId);
  return frames.reduce((s, f) => s + (f.blob.size || 0), 0);
}

export async function totalBytes(userId) {
  const meta = await listMeta(userId);
  let total = meta.reduce((s, m) => s + (m.audioBytes || 0), 0);
  // Per-recording read is fine: N is small (a single user's draft list — tens at most).
  for (const m of meta) {
    const frames = await listFrames(userId, m.id);
    total += frames.reduce((s, f) => s + (f.blob.size || 0), 0);
  }
  return total;
}
